import {DOM} from '../helpers/DOM.js';
import {Helper} from '../helpers/Helper.js';

export class MyAccountGamificationParticipantDashboard {
	#dashboardData = {};
	#dashboard;

	constructor() {
		this.#dashboard = DOM.el('.gamification-dashboard-container');
		if (!this.#dashboard) {
			return;
		}

		this.#initProgressBars();
		this.#fetchData();
		this.#fetchFundraisingSummary();
	}

	#initProgressBars(percentage = null) {
		const progressEls = DOM.els(
			'.profile-progress-bar-wrapper .gamification-dashboard-progressbar .levcharity_progressbar',
			this.#dashboard
		);

		progressEls.forEach((progress) => {
			const value =
				percentage !== null && percentage !== undefined
					? percentage
					: progress.dataset.percentage ?? '0';

			const width = `${value}%`;
			progress.style.setProperty('--width', width);
			progress.style.width = width;
			progress.style.setProperty('--opacity', '1');
			progress.removeAttribute('data-percentage');
		});
	}

	async #fetchData() {
		const {Helper} = await import('../helpers/Helper.js' /* webpackChunkName: "helper" */);

		Helper.request(admin_ajax[0], {
			action: this.#dashboard?.dataset.action,
			token: this.#dashboard?.dataset.token,
		}).then((response) => {
			if (response.success) {
				this.#dashboardData = {
					participantData: response.participant_data,
					levelData: response.level_data,
				};
				this.#renderProfile();
			}
		});
	}

	#renderProfile() {
		const participantData = this.#dashboardData.participantData;
		const levelData = this.#dashboardData.levelData;
		const profileTotalsWrapper = DOM.el('.profile-totals-wrapper');
		const totalDonationCount = DOM.el('.total-donations .count-wrapper .count', profileTotalsWrapper);
		const totalDonorsCount = DOM.el('.total-donors .count-wrapper .count', profileTotalsWrapper);
		const currencySymbol = Helper.resolveCurrencySymbol(this.#dashboard.dataset.currencySymbol);

		this.#numberAnimation(participantData.total_amount, totalDonationCount, currencySymbol);
		this.#numberAnimation(levelData.donors_count, totalDonorsCount, '', '', 0, false);

		const progressBar = DOM.el('.profile-progress-bar-wrapper');
		const countWrapper = DOM.el('.count-wrapper', progressBar);
		const nextBadgeCountWrapper = DOM.el('.next-badge-count-wrapper', progressBar);
		const progressTarget =
			levelData.maxAmountRange || levelData.userNextLevel?.[0]?.amountRange?.min || 0;

		this.#numberAnimation(levelData.userNeedToNextLevel, DOM.el('.count', nextBadgeCountWrapper), currencySymbol);
		this.#numberAnimation(
			levelData.userNeedToNextLevel,
			DOM.el('.need-to-next-level-mobile', progressBar),
			currencySymbol
		);
		this.#numberAnimation(levelData.userTotalRaised, DOM.el('.current-count', countWrapper), currencySymbol);
		this.#numberAnimation(progressTarget, DOM.el('.max-count', countWrapper), currencySymbol);

		const raised = Number(levelData.userTotalRaised) || 0;
		const target = Number(progressTarget) || 0;
		const progressPercentage =
			target > 0 ? Math.min((raised / target) * 100, 100) : 0;

		this.#initProgressBars(progressPercentage);
	}

	async #fetchFundraisingSummary() {
		const summary = DOM.el('.gamification-fundraising-summary', this.#dashboard);

		if (!summary?.dataset.action) {
			return;
		}

		const {Helper} = await import('../helpers/Helper.js' /* webpackChunkName: "helper" */);

		Helper.request(admin_ajax[0], {
			action: summary.dataset.action,
			token: summary.dataset.token,
			timeline: '1y',
		}).then((response) => {
			if (response.success) {
				this.#renderFundraisingSummary(summary, response);
			}
		});
	}

	#renderFundraisingSummary(summary, data) {
		const currencySymbol = Helper.resolveCurrencySymbol(
			summary.dataset.currencySymbol,
			this.#dashboard.dataset.currencySymbol
		);
		const progressbar = DOM.el('.progress-bar-wrapper', summary);
		const totalsToday = DOM.el('.totals_total', summary);
		const raised = DOM.el('.total_raised', summary);
		const goal = DOM.el('.goal', summary);
		const percentageEl = DOM.el('.circular-progress-percentage', summary);
		const goalValue = Number(data.goal) || 0;
		const raisedValue = Number(data.total_raised) || 0;
		const percentage = goalValue > 0 ? Math.min(100, raisedValue / (goalValue / 100)) : 0;

		if (progressbar) {
			progressbar.style.setProperty('--progress', String(percentage));
		}

		const todayText = this.#formatAnimatedAmount(data.total_today, currencySymbol);
		const raisedText = this.#formatAnimatedAmount(data.total_raised, currencySymbol);
		const goalText = this.#formatAnimatedAmount(data.goal, currencySymbol);

		this.#lockAnimatedTextWidth(totalsToday, todayText);
		this.#lockAnimatedTextWidth(raised, raisedText);
		this.#lockAnimatedTextWidth(goal, goalText);
		this.#lockAnimatedTextWidth(percentageEl, '100%');

		this.#numberAnimation(data.total_today, totalsToday, currencySymbol);
		this.#numberAnimation(data.total_raised, raised, currencySymbol);
		this.#numberAnimation(data.goal, goal, currencySymbol);
		this.#numberAnimation(percentage, percentageEl, '', '%', 0, false);
	}

	#formatAnimatedAmount(value, currencySymbol, withFractions = true) {
		return currencySymbol + Number(value || 0).toFixed(withFractions ? 2 : 0);
	}

	#lockAnimatedTextWidth(el, text) {
		if (!el || text === undefined || text === null) {
			return;
		}

		el.style.display = 'inline-block';
		el.style.whiteSpace = 'nowrap';
		el.style.minWidth = `${this.#measureTextWidth(el, String(text))}px`;
	}

	#measureTextWidth(el, text) {
		const probe = document.createElement('span');
		const style = window.getComputedStyle(el);

		probe.textContent = text;
		probe.style.cssText = [
			'position:absolute',
			'visibility:hidden',
			'white-space:nowrap',
			'pointer-events:none',
			`font:${style.font}`,
			`font-size:${style.fontSize}`,
			`font-weight:${style.fontWeight}`,
			`font-family:${style.fontFamily}`,
			`letter-spacing:${style.letterSpacing}`,
		].join(';');

		document.body.appendChild(probe);
		const width = probe.offsetWidth;
		probe.remove();

		return width;
	}

	async #numberAnimation(to, el, prefix, suffix = '', from = 0, withFractions = true) {
		const {gsap} = await import('gsap' /* webpackChunkName: "gsap" */);

		gsap.to(
			{value: from},
			{
				value: +to,
				duration: 0.8,
				ease: 'power2.inOut',
				onUpdate: function () {
					if (el) {
						el.textContent = prefix + this.targets()[0].value.toFixed(withFractions ? 2 : 0) + suffix;
					}
				},
			}
		);
	}

}
